import React, { useState } from 'react';
import { Typography, Tooltip, Button } from 'antd';
import { 
  FullscreenOutlined, 
  EditOutlined,
  DownloadOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { copyToClipboard, downloadImage } from '../utils';

const InputsSection = ({ images, onImageClick, onEditImage }) => {
  const [hoveredId, setHoveredId] = useState(null);

  if (!images || images.length === 0) return null;

  return (
    <div className="jdv-section jdv-inputs-section">
      <div className="jdv-section-header">
        <Typography.Title level={5} className="jdv-section-title">
          📥 Input Images
        </Typography.Title>
        <span className="jdv-count-badge">{images.length}</span>
      </div>

      <div className="jdv-images-grid">
        {images.map((image) => (
          <div
            key={image.id}
            className="jdv-image-card"
            onMouseEnter={() => setHoveredId(image.id)}
            onMouseLeave={() => setHoveredId(null)}
            // Only images open the viewer — it refuses video/audio, so wiring
            // the click on those cards would be a dead interaction that also
            // strands fullViewerImage and swallows the next close.
            onClick={image.type === 'image' ? () => onImageClick?.(image, images) : undefined}
          >
            <div className="jdv-image-preview">
              {image.type === 'video' ? (
                <video src={image.url} muted playsInline preload="metadata" controls />
              ) : image.type === 'audio' ? (
                <audio src={image.url} controls preload="metadata" style={{ width: '100%' }} />
              ) : (
                <img
                  src={image.url}
                  alt={image.label}
                  loading="lazy"
                />
              )}
              
              {/* Hover overlay — images only. On video/audio it would cover the
                  native player controls and offer a viewer that won't open. */}
              {hoveredId === image.id && image.type === 'image' && (
                <div className="jdv-image-overlay">
                  <div className="jdv-image-actions">
                    <Tooltip title="View Full">
                      <Button
                        type="primary"
                        size="small"
                        icon={<FullscreenOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onImageClick?.(image, images);
                        }}
                      />
                    </Tooltip>

                    {onEditImage && (
                      <Tooltip title="Edit Image">
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditImage?.(image.url);
                          }}
                        />
                      </Tooltip>
                    )}
                    
                    <Tooltip title="Download">
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadImage(image.url, `input_${image.id}`);
                        }}
                      />
                    </Tooltip>
                    
                    <Tooltip title="Copy URL">
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(image.url);
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
            
            <div className="jdv-image-label">
              <Typography.Text ellipsis>
                {image.label}
              </Typography.Text>
              <span className="jdv-image-source">{image.source}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InputsSection;
